import React, { useEffect, useState } from "react";
import { Input, Checkbox } from "antd";
import { observer } from "mobx-react";
import classNames from "classnames";
import BaseModal from "@app/components/modal";
import Loader from "@app/components/loader/content";
import notify from "@app/components/notify";

import chatStore from "../../state/store/ai-chat";

import "./style/articles-picker.scoped.scss";

const TITLE = { slr: "Pick DUE Articles", sota: "Pick SoTA Articles" };

const ArticlesPicker = observer(({ type, files, onCancel, onSelect }) => {
    const [articles, setArticles] = useState(null);
    const [filtered, setFiltered] = useState([]);
    const [filter, setFilter] = useState("");
    const [selected, setSelected] = useState({});

    const selectAllState = React.useMemo(() => {
        if (filtered.length === 0) {
            return { checked: false, indeterminate: false };
        }

        const selectedCount = filtered.filter((article) => selected[article._id]).length;

        if (selectedCount === 0) {
            return { checked: false, indeterminate: false };
        }

        if (selectedCount === filtered.length) {
            return { checked: true, indeterminate: false };
        }

        return { checked: false, indeterminate: true };
    }, [filtered, selected]);

    function onSelectAllToggle() {
        const newSelected = { ...selected };
        const shouldSelect = !selectAllState.checked;

        filtered.forEach((article) => {
            newSelected[article._id] = shouldSelect;
        });

        setSelected(newSelected);
    }

    useEffect(() => {
        const selected = {};

        (files ?? [])
            .filter((file) => file.type === "article" && file.articleType === type)
            .forEach((file) => {
                selected[file._id] = true;
            });

        setSelected({ ...selected });
    }, [articles]);

    useEffect(() => {
        setFiltered(filterArticles());
    }, [articles, filter]);

    /**
     * Filter the list of articles by search tokens (title or CID)
     */
    function filterArticles() {
        const list = articles ?? [];

        const tokens = filter
            .split(/\s+/g)
            .filter((token) => !!token)
            .map((token) => token.toLowerCase());

        if (tokens.length === 0) {
            return list;
        }

        return list.filter((article) => {
            const haystack = `c${article.mdrId ?? ""} ${article.mdrId ?? ""} ${
                article.title ?? ""
            }`.toLowerCase();

            return tokens.every((token) => haystack.includes(token));
        });
    }

    /**
     * Load the list of selectable literature articles for this category
     */
    async function load() {
        try {
            setArticles(await chatStore.loadLiteratureArticles(type));
        } catch (ex) {
            notify.error("Error loading the literature articles");
            setArticles([]);
        }
    }

    /**
     * Handle selection toggle
     */
    function onToggle(articleId) {
        selected[articleId] = !selected[articleId];
        setSelected({ ...selected });
    }

    /**
     * Convert the selection into context items shaped for the chat prompt
     */
    function handleSelection() {
        const picked = (articles ?? [])
            .filter((article) => selected[article._id])
            .map((article) => ({
                _id: article._id,
                name: article.title,
                type: "article",
                articleType: article.type,
                mdrId: article.mdrId,
            }));

        onSelect(picked);
    }

    useEffect(() => {
        load();
    }, []);

    return (
        <BaseModal
            title={TITLE[type] ?? "Pick Literature Articles"}
            visible={true}
            onCancel={onCancel}
            onOk={handleSelection}
            okText="Select"
            width={600}
            height="50vh"
            className={classNames("articles-picker")}
        >
            <div className="container">
                {articles && (
                    <>
                        <div className="search">
                            <Input.Search
                                placeholder="Search by title or CID"
                                onSearch={(filter) => setFilter(filter)}
                                allowClear
                            />
                        </div>

                        {filtered.length > 0 && (
                            <div className="select-all" onClick={onSelectAllToggle}>
                                <div className="checkbox">
                                    <Checkbox
                                        checked={selectAllState.checked}
                                        indeterminate={selectAllState.indeterminate}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={onSelectAllToggle}
                                    />
                                </div>
                                <div className="label">
                                    Select All ({filtered.length} article
                                    {filtered.length !== 1 ? "s" : ""})
                                </div>
                            </div>
                        )}

                        <div className="articles">
                            {filtered.map((article) => {
                                return (
                                    <div
                                        className="article"
                                        key={article._id}
                                        onClick={() => onToggle(article._id)}
                                    >
                                        <div className="checkbox">
                                            <Checkbox
                                                checked={!!selected[article._id]}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => onToggle(article._id)}
                                            />
                                        </div>
                                        <div className="name">
                                            {article.mdrId ? `C${article.mdrId} - ` : ""}
                                            {article.title}
                                        </div>
                                    </div>
                                );
                            })}

                            {filtered.length === 0 && (
                                <div className="empty">No literature articles available.</div>
                            )}
                        </div>
                    </>
                )}

                {!articles && (
                    <div className="loading">
                        <Loader loading={true} />
                    </div>
                )}
            </div>
        </BaseModal>
    );
});

export default ArticlesPicker;
